import qs from 'qs';
import moment from 'moment';
import api from './api';
import AxiosUtil from '../utils/AxiosUtil';
import generateSearchRequest from '../utils/sequelize-search-builder-client';

class UserDto {
	constructor(user) {
		this.id = user.id;
		this.baseId = user.baseId;
		this.name = user.name;
		this.email = user.email;
		this.photo = user.photo;
		this.type = user.type;
		this.cpf = user.cpf;
		this.rg = user.rg;
		this.phone = user.phone;
		this.modal = user.modal;
		this.salary = user.salary;

		this.base = user.base || null;
		this.address = user.address || null;
		this.bankInfo = user.bankInfo || null;

		this.createdAt = user.createdAt ? moment(user.createdAt).toDate() : null;
		this.updatedAt = user.updatedAt ? moment(user.updatedAt).toDate() : null;
		this.deletedAt = user.deletedAt ? moment(user.deletedAt).toDate() : null;
	}

	isAdministrator() {
		return this.type === 'ADMINISTRADOR';
	}

	isDeliveryman() {
		return this.type === 'ENTREGADOR';
	}

	isColaborator() {
		return this.type === 'COLABORADOR';
	}

	static fromUser(user) {
		return new UserDto(user);
	}
}

const selectUser = (user) => {
	return {
		...user,
		createdAt: user.createdAt ? moment(user.createdAt).toDate() : null,
		updatedAt: user.updatedAt ? moment(user.updatedAt).toDate() : null,
		deletedAt: user.deletedAt ? moment(user.deletedAt).toDate() : null,
	};
};

const selectUsers = (users) => users.map(selectUser);

class UserService {
	async getAll({ page = 0, size = 10, order, filters }) {
		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				baseId: {
					as: 'baseId',
					type: 'equal',
				},
				name: {
					as: `name`,
					type: 'like',
				},
				email: {
					as: 'email',
					type: 'like',
				},
				type: {
					as: 'type',
					type: 'like',
				},
				cpf: {
					as: 'cpf',
					type: 'like',
				},
				rg: {
					as: 'rg',
					type: 'like',
				},
				phone: {
					as: 'phone',
					type: 'like',
				},
				zone: {
					as: 'zone',
					type: 'like',
				},
				baseName: {
					as: 'base.name',
					type: 'like',
				},
				'createdAt:between': {
					as: 'createdAt',
					type: 'between',
				},
			},
			order,
		};
		const request = generateSearchRequest(filters, settings);
		return api
			.get('/users', {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: {
					page,
					size,
					...request,
				},
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectUsers(data.body) }))
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to get users in UserService.'),
			);
	}

	async getCurrentUser() {
		return api
			.get('/users/me')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch(AxiosUtil.handleError.bind(AxiosUtil, 'fail when getCurrentUser in UserService.'));
	}

	async get(id) {
		return api
			.get(`/users/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectUser)
			.catch(AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to get user in UserService.'));
	}

	async login(email, password) {
		return api
			.post('/users/login', { email, password })
			.then((response) => response.data)
			.then((data) => data.body)
			.catch(AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to login in UserService.'));
	}

	async create(userForm) {
		return api
			.post('/users', userForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectUser)
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to create user in UserService.'),
			);
	}

	async remove(id) {
		return api
			.delete(`/users/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to remove user in UserService.'),
			);
	}

	async update(id, userForm) {
		return api
			.patch(`/users/${id}`, userForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectUser)
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to update user in UserService.'),
			);
	}

	async updateMe(userForm) {
		return api
			.patch('/users/me', userForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectUser)
			.catch(
				AxiosUtil.handleError.bind(
					AxiosUtil,
					'fail when trying to update the current user in UserService.',
				),
			);
	}

	async getUserTypes() {
		return api
			.get('/users/types')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to get user types in UserService.'),
			);
	}

	async getModals() {
		return api
			.get('/users/modals')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to get modals in UserService.'),
			);
	}

	async downloadCsv() {
		return api
			.get('/users/download')
			.then((response) => response.data)
			.then((data) => new Blob([data], { type: 'application/csv' }))
			.then((blob) => URL.createObjectURL(blob))
			.then((uril) => {
				let link = document.createElement('a');
				link.setAttribute('href', uril);
				link.setAttribute('download', 'usuarios.csv');
				let clickEvent = new MouseEvent('click', {
					view: window,
					bubbles: true,
					cancelable: false,
				});
				link.dispatchEvent(clickEvent);
			})
			.catch(
				AxiosUtil.handleError.bind(AxiosUtil, 'fail when trying to download csv in UserService.'),
			);
	}
}

const userService = new UserService();
export default userService;
